import { useState, useCallback, ReactNode, useMemo, useRef, useEffect } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AskRequest } from '@hone-automation/common';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { MobileHeader } from '../MobileHeader';
import { MobileBottomNav, TabKey } from '../MobileBottomNav';
import { MobileCatchUp } from '../MobileCatchUp';
import { MobileRequestList, MobileRequestListRef } from '../MobileRequestList';
import { MobileDetailsScreen } from '../MobileDetailsScreen';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel';
import { MobileCreateRequestPanel } from '../MobileCreateRequestPanel/MobileCreateRequestPanel';
import { MobileCreateEmailPanel } from '../MobileCreateEmailPanel/MobileCreateEmailPanel';
import { MobileCreateGroupPanel } from '../MobileCreateGroupPanel/MobileCreateGroupPanel';
import { EmptyState } from '../../Common/EmptyState';
import {
  IconTaskNew,
  IconNotifications,
  IconAddNew,
  IconEmailNew,
  IconFolder,
  IconChevron,
  IconPendoInfo,
} from 'components/Icons';
import RequestType from '../../Common/RequestType/RequestType';
import { useFlags } from 'launchdarkly-react-client-sdk';
import IconButton from '../../Common/IconButton';
import KSLocationModal from 'presentation/components/KSLocationModal';
import { useUserLocationsQuery } from 'hooks/useUserLocationsQuery';
import { useRecentLocations, MAX_RECENT_LOCATIONS } from 'hooks/useRecentLocations';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useAuthContext } from 'context/useAuthContext';
import { useFetchQueueRequestById } from 'hooks/queue';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useGroups } from '../../../Dashboard/useGroups';
import './MobileKSQ2Layout.scss';

// Details panel slide configuration
const EDGE_THRESHOLD = 30; // Touch must start within this distance from left edge
const SWIPE_THRESHOLD = 100; // Swipe distance to trigger close

/**
 * MobileKSQ2Layout - Main layout wrapper for mobile KSQ2 experience
 *
 * Structure:
 * - MobileHeader (top): Location selector + logout
 * - Content card (middle): Tab content with rounded top corners
 * - MobileBottomNav (bottom): Tab navigation
 *
 * Navigation:
 * - Manages active tab state
 * - Routes to appropriate content component
 * - Handles conversation screen push/pop (future)
 *
 * @see RN equivalent: ks-mobile-app/src/components/layout/TabScreenLayout.tsx
 * @see RN navigation: ks-mobile-app/src/navigation/TabsNavigator.tsx
 */

interface MobileKSQ2LayoutProps {
  /** Chart component to pass to CatchUp tab */
  chart?: ReactNode;
  /** Whether data is still loading */
  isLoading?: boolean;
}

/** Screen title and icon configuration per tab */
const TAB_CONFIG: Record<TabKey, { title: string; icon: ReactNode }> = {
  catchup: { title: 'CATCHUP', icon: <IconNotifications /> },
  kai: { title: 'KAI', icon: null },
  todos: { title: 'TO-DOS', icon: <IconTaskNew /> },
  calendar: { title: 'CALENDAR', icon: null },
  chats: { title: 'CHATS', icon: null },
};

export const MobileKSQ2Layout = ({ chart, isLoading = false }: MobileKSQ2LayoutProps) => {
  const prefix = prefixClass('ksq2-mobile-layout');
  const navigate = useNavigate();
  const { locationId: currentLocationId } = useParams();
  const [, setSearchParams] = useSearchParams();

  // Active tab state - default to 'todos' for Phase 1
  const [activeTab, setActiveTab] = useState<TabKey>('todos');

  // Details screen state - tracks which request is being viewed in detail
  const [detailsRequest, setDetailsRequest] = useState<AskRequest | null>(null);

  // Details panel slide state
  const [panelOffset, setPanelOffset] = useState(0); // 0 = fully visible, positive = sliding right
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Get requests and selectedItem from context for live updates
  const { state, dispatch: queueDispatch } = useQueueContext();
  const { requests, selectedItem, activeView, activeGroupId } = state;

  // Sync detailsRequest with context when panel is open
  // This ensures updates from CMComposer, AssignmentButton, etc. are reflected
  // We check both selectedItem (if IDs match) and the requests array (as fallback)
  // because selectedItem might be set to a different request after status changes
  useEffect(() => {
    if (isPanelOpen && detailsRequest) {
      // First try selectedItem if it's the same request
      if (selectedItem && selectedItem.id === detailsRequest.id) {
        setDetailsRequest(selectedItem);
      } else {
        // Fallback: find the updated request in the requests array
        const updatedRequest = requests.find(r => r.id === detailsRequest.id);
        if (updatedRequest) {
          setDetailsRequest(updatedRequest);
        }
      }
    }
  }, [isPanelOpen, detailsRequest?.id, selectedItem, requests]);
  const panelRef = useRef<HTMLDivElement>(null);
  const swipeStartX = useRef<number | null>(null);
  const isEdgeSwipe = useRef(false);

  // Request list ref for scroll control
  const requestListRef = useRef<MobileRequestListRef>(null);
  const headerLastTapRef = useRef<number>(0);
  const DOUBLE_TAP_THRESHOLD = 300; // ms

  // Groups - derive active group name for header
  const groups = useGroups();
  const isInGroupView = activeView === 'Groups' && !!activeGroupId;
  const activeGroupName = useMemo(() => {
    if (!activeGroupId) return null;
    return groups.find(g => g.id === activeGroupId)?.name ?? null;
  }, [groups, activeGroupId]);

  const handleExitGroupView = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();
      queueDispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: null });
      queueDispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Me' });
    },
    [queueDispatch]
  );

  // Location modal state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Create panel state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatePickerOpen, setIsCreatePickerOpen] = useState(false);
  const [isCreateEmailOpen, setIsCreateEmailOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);

  // Feature flag for email/group create options
  const { enableCreateQueueEmailModal } = useFlags();
  const { isBookkeeperAdmin } = useAuthContext();
  const showCreateOptions = isBookkeeperAdmin && enableCreateQueueEmailModal;

  // Resolve location name
  const allLocationsState = useLocationsStore(state => state.allLocations);
  const allLocations = Array.isArray(allLocationsState) ? allLocationsState : [];
  const locationName = allLocations.find(
    loc => loc.id === currentLocationId || (loc as any).locationId === currentLocationId
  );
  const locationNameStr = (locationName && ((locationName as any).locationName || (locationName as any).name)) || '';

  const fetchQueueRequestById = useFetchQueueRequestById();

  // Fetch user locations for the modal
  const { data: locations = [] } = useUserLocationsQuery();
  const { recentLocations, setRecentLocations } = useRecentLocations();

  // Handle tab change
  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    // Clear details screen when changing tabs
    setDetailsRequest(null);
  }, []);

  // Navigate from CatchUp stats to To-Dos with filter
  const handleNavigateToTodos = useCallback((_filter?: string) => {
    setActiveTab('todos');
    // TODO: Apply filter to To-Dos list in Phase 2
  }, []);

  // Open details screen for a request (slide panel in from right)
  const handleOpenDetails = useCallback(
    (request: AskRequest) => {
      setDetailsRequest(request);
      setIsPanelOpen(true);
      // Start off-screen to the right
      setPanelOffset(window.innerWidth);
      // Update URL with requestId for deep linking
      setSearchParams(prev => {
        prev.set('requestId', String(request.id));
        return prev;
      });
      // Animate in after a frame (allows CSS to register initial position)
      requestAnimationFrame(() => {
        setIsAnimating(true);
        setPanelOffset(0);
        // Clear animating state after animation completes
        setTimeout(() => setIsAnimating(false), 300);
      });
    },
    [setSearchParams]
  );

  const handleGroupNameTap = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (!activeGroupId) return;
      const groupRequest = requests.find(r => r.id === activeGroupId) || (await fetchQueueRequestById(activeGroupId));
      if (groupRequest) {
        queueDispatch({ type: 'SET_SELECTED_ITEM', payload: groupRequest });
        handleOpenDetails(groupRequest);
      }
    },
    [activeGroupId, requests, fetchQueueRequestById, queueDispatch, handleOpenDetails]
  );

  // Go back from details screen to list (slide panel out to right)
  const handleBackToList = useCallback(() => {
    setIsAnimating(true);
    setPanelOffset(window.innerWidth); // Slide fully off-screen
    // After animation, actually close the panel
    setTimeout(() => {
      setIsPanelOpen(false);
      setDetailsRequest(null);
      setPanelOffset(0);
      setIsAnimating(false);
    }, 300);
    // Keep requestId in URL so the card stays selected
  }, []);

  // Edge swipe gesture for panel close
  useEffect(() => {
    if (!isPanelOpen) return;

    const panel = panelRef.current;
    if (!panel) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      // Only start edge swipe if touch begins near left edge of panel
      if (touch.clientX <= EDGE_THRESHOLD) {
        swipeStartX.current = touch.clientX;
        isEdgeSwipe.current = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isEdgeSwipe.current || swipeStartX.current === null) return;

      const touch = e.touches[0];
      const distance = touch.clientX - swipeStartX.current;

      if (distance > 0) {
        e.preventDefault();
        setPanelOffset(distance);
      } else {
        // Swiping left, cancel
        isEdgeSwipe.current = false;
        setPanelOffset(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isEdgeSwipe.current) return;

      if (panelOffset >= SWIPE_THRESHOLD) {
        // Close the panel
        handleBackToList();
      } else {
        // Snap back
        setIsAnimating(true);
        setPanelOffset(0);
        setTimeout(() => setIsAnimating(false), 200);
      }

      swipeStartX.current = null;
      isEdgeSwipe.current = false;
    };

    panel.addEventListener('touchstart', handleTouchStart, { passive: true });
    panel.addEventListener('touchmove', handleTouchMove, { passive: false });
    panel.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      panel.removeEventListener('touchstart', handleTouchStart);
      panel.removeEventListener('touchmove', handleTouchMove);
      panel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPanelOpen, panelOffset, handleBackToList]);

  // Location modal handlers
  const handleOpenLocationModal = useCallback(() => {
    setIsLocationModalOpen(true);
  }, []);

  const handleCloseLocationModal = useCallback(() => {
    setIsLocationModalOpen(false);
  }, []);

  // Create modal handlers
  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateClick = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation(); // Prevent header tap handler
      if (showCreateOptions) {
        setIsCreatePickerOpen(true);
      } else {
        handleOpenCreateModal();
      }
    },
    [handleOpenCreateModal, showCreateOptions]
  );

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleLocationChange = useCallback(
    (locationId: string) => {
      // Navigate to new location
      navigate(`/app/location/${locationId}/new-dashboard`);
      setIsLocationModalOpen(false);
    },
    [navigate]
  );

  const handleAddToRecentLocations = useCallback(
    (locationId: string) => {
      const selectedLocation = locations.find(loc => loc.locationId === locationId);
      if (!selectedLocation) return;
      setRecentLocations(prevRecentLocations => {
        const updatedLocations = [
          selectedLocation,
          ...prevRecentLocations.filter(loc => loc.locationId !== locationId),
        ];
        const newRecentLocations = updatedLocations.slice(0, MAX_RECENT_LOCATIONS);
        localStorage.setItem('recentLocations', JSON.stringify(newRecentLocations));
        return newRecentLocations;
      });
    },
    [locations, setRecentLocations]
  );

  const handleRemoveFromRecentLocations = useCallback(
    (locationId: string) => {
      setRecentLocations(prevRecentLocations => {
        const updatedLocations = prevRecentLocations.filter(loc => loc.locationId !== locationId);
        const newRecentLocations = updatedLocations.slice(0, MAX_RECENT_LOCATIONS);
        localStorage.setItem('recentLocations', JSON.stringify(newRecentLocations));
        return newRecentLocations;
      });
    },
    [setRecentLocations]
  );

  // Card header tap handler: single tap = scroll to selected, double tap = scroll to top
  const handleCardHeaderTap = useCallback(() => {
    if (activeTab !== 'todos') return;

    const now = Date.now();
    if (now - headerLastTapRef.current < DOUBLE_TAP_THRESHOLD) {
      // Double tap - scroll to top
      requestListRef.current?.scrollToTop();
      headerLastTapRef.current = 0; // Reset to prevent triple-tap
    } else {
      // Single tap - scroll to selected (with delay to detect double tap)
      headerLastTapRef.current = now;
      setTimeout(() => {
        // Only scroll to selected if no second tap occurred
        if (headerLastTapRef.current === now) {
          requestListRef.current?.scrollToSelected();
        }
      }, DOUBLE_TAP_THRESHOLD);
    }
  }, [activeTab]);

  // Get current tab configuration
  const currentTabConfig = TAB_CONFIG[activeTab];

  // Calculate CatchUp badge count (placeholder for now)
  // TODO: Implement actual notification/blocker count
  const catchUpBadge = 0;

  // Render content based on active tab (details panel is rendered separately)
  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className={prefix('__loading')}>
          <span>Loading...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'catchup':
        return <MobileCatchUp chart={chart} onNavigateToTodos={handleNavigateToTodos} />;

      case 'todos':
        // Always render list - details is a separate sliding panel
        return <MobileRequestList ref={requestListRef} onRequestSelect={handleOpenDetails} />;

      case 'kai':
      case 'calendar':
      case 'chats':
      default:
        return (
          <EmptyState
            icon={currentTabConfig.icon || <IconTaskNew />}
            title="Coming Soon"
            description="This feature is not yet available"
          />
        );
    }
  }, [activeTab, isLoading, chart, handleNavigateToTodos, currentTabConfig.icon, prefix, handleOpenDetails]);

  return (
    <>
      <div className={prefix()}>
        {/* Header */}
        <MobileHeader onLocationPress={handleOpenLocationModal} />

        {/* Content Card */}
        <div className={prefix('__card')}>
          {/* Screen Title - tap to scroll to selected, double-tap to scroll to top */}
          <div className={prefix('__card-header')} onClick={handleCardHeaderTap} role="button" tabIndex={0}>
            {/* Left slot: back button in group view, task icon otherwise */}
            {isInGroupView && activeTab === 'todos' ? (
              <IconButton
                icon={<IconChevron />}
                size="large"
                className={prefix('__back-btn')}
                onClick={handleExitGroupView}
              />
            ) : (
              currentTabConfig.icon && <span className={prefix('__card-header-icon')}>{currentTabConfig.icon}</span>
            )}
            <div className={prefix('__card-header-titles')}>
              <span className={prefix('__card-header-title')}>{currentTabConfig.title}</span>
              {isInGroupView && <RequestType type="Group" size="default" showTooltip={false} />}
            </div>
            {/* Create button - always visible on todos tab */}
            {activeTab === 'todos' && (
              <div className={prefix('__create-btn')} onClick={handleCreateClick}>
                <IconButton
                  icon={<IconAddNew />}
                  size="large"
                  tooltip="Create To-Do"
                  rounded
                  onClick={handleCreateClick}
                />
              </div>
            )}
          </div>

          {/* Group subheader - clickable row showing group name below the header */}
          {isInGroupView && activeGroupName && (
            <button type="button" className={prefix('__card-group-subheader')} onClick={handleGroupNameTap}>
              <span className={prefix('__card-group-subheader-name')}>{activeGroupName}</span>
              <span className={prefix('__card-group-subheader-icon')}>
                <IconPendoInfo />
              </span>
            </button>
          )}

          {/* Content Area */}
          <div className={prefix('__card-content')}>{renderContent}</div>
        </div>

        {/* Bottom Navigation - hidden until other tabs are implemented */}
        {/* <MobileBottomNav activeTab={activeTab} onTabChange={handleTabChange} catchUpBadge={catchUpBadge} /> */}
      </div>

      {/* Details Panel - slides in from right, covers entire screen */}
      {isPanelOpen && detailsRequest && (
        <>
          {/* Backdrop */}
          <div
            className={classNames(prefix('__details-backdrop'), {
              visible: panelOffset < window.innerWidth * 0.5,
            })}
            style={{ opacity: 1 - panelOffset / window.innerWidth }}
            onClick={handleBackToList}
          />

          {/* Sliding Panel */}
          <div
            ref={panelRef}
            className={classNames(prefix('__details-panel'), {
              animating: isAnimating,
            })}
            style={{ transform: `translateX(${panelOffset}px)` }}
          >
            <MobileDetailsScreen request={detailsRequest} onBack={handleBackToList} />
          </div>
        </>
      )}

      {/* Location Modal */}
      <div
        className={classNames(prefix('__location-modal'), { open: isLocationModalOpen })}
        onClick={e => e.stopPropagation()}
      >
        <KSLocationModal
          handleClose={handleCloseLocationModal}
          recentLocations={recentLocations}
          activeLocation={currentLocationId}
          onLocationChange={handleLocationChange}
          onAddToRecentLocations={handleAddToRecentLocations}
          onRemoveFromRecentLocations={handleRemoveFromRecentLocations}
          isOpen={isLocationModalOpen}
          locations={locations}
          mobile
        />
      </div>

      {/* Create Request Panel */}
      <MobileCreateRequestPanel
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        locationName={locationNameStr}
      />

      {/* Create Email Panel */}
      <MobileCreateEmailPanel
        isOpen={isCreateEmailOpen}
        onClose={() => setIsCreateEmailOpen(false)}
        locationName={locationNameStr}
      />

      {/* Create Group Panel */}
      <MobileCreateGroupPanel isOpen={isCreateGroupOpen} onClose={() => setIsCreateGroupOpen(false)} />

      {/* Create Options Picker (behind feature flag) */}
      <MobileSlideUpPanel
        isOpen={isCreatePickerOpen}
        onClose={() => setIsCreatePickerOpen(false)}
        title="Create"
        useFitContent
        resizable={false}
      >
        <div className={prefix('__create-options')}>
          <button
            type="button"
            className={prefix('__create-option')}
            onClick={() => {
              setIsCreatePickerOpen(false);
              setIsCreateModalOpen(true);
            }}
          >
            <span className={prefix('__create-option-icon')}>
              <IconTaskNew />
            </span>
            <span className={prefix('__create-option-label')}>New To-Do</span>
          </button>
          <button
            type="button"
            className={prefix('__create-option')}
            onClick={() => {
              setIsCreatePickerOpen(false);
              setIsCreateEmailOpen(true);
            }}
          >
            <span className={prefix('__create-option-icon')}>
              <IconEmailNew />
            </span>
            <span className={prefix('__create-option-label')}>New Email</span>
          </button>
          <button
            type="button"
            className={prefix('__create-option')}
            onClick={() => {
              setIsCreatePickerOpen(false);
              setIsCreateGroupOpen(true);
            }}
          >
            <span className={prefix('__create-option-icon', 'group')}>
              <IconFolder />
            </span>
            <span className={prefix('__create-option-label')}>New Group</span>
          </button>
        </div>
      </MobileSlideUpPanel>

      {/* Portal container for slide-up panels (advanced filters, sort options, etc.)
          Rendered at layout level for consistent z-index stacking above all content */}
      <div id="mobile-panels-root" />
    </>
  );
};

export default MobileKSQ2Layout;
